// Unified API utility with comprehensive logging
import axios, { AxiosResponse, AxiosRequestConfig, AxiosError, InternalAxiosRequestConfig } from 'axios';
import ENV from './env';
import Logger from './logger';
import { showAlert } from './alert';
import { showError, NETWORK_ERROR_MESSAGES } from './toast';

// Base API configuration
let API_BASE_URL = ENV.API_URL || 'http://localhost:8005';

// Force HTTPS in production
if (ENV.PROD && API_BASE_URL.startsWith('http://')) {
  console.warn('⚠️ Converting HTTP to HTTPS in production');
  API_BASE_URL = API_BASE_URL.replace('http://', 'https://');
}

// Debug API URL in production
if (ENV.PROD) {
  console.log('🔧 Production API URL:', API_BASE_URL);
}

// Create main axios instance (with interceptors)
const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
  timeout: 30000, // 30 seconds timeout
});

// Create raw axios instance WITHOUT interceptors (สำหรับ refresh token call เพื่อหลีกเลี่ยง loop)
const rawAxios = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

// Log the actual base URL being used
if (!import.meta.env.PROD) {
  console.log('🌐 Axios Base URL:', api.defaults.baseURL);
}

// ==================== Refresh Token Queue Pattern ====================
// failedQueue ยังคงจำเป็น: เมื่อมีหลาย request เจอ 401 พร้อมกันในแท็บเดียว เราจะ queue
// request เหล่านั้นไว้รอ single-flight refresh เสร็จ แล้วค่อย retry ทีเดียว
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// ==================== S1144 FIX (OBS-S1142-001): Cross-Tab Refresh Coordination ====================
// Two browser tabs detecting 401 simultaneously could each trigger their own /auth/refresh call.
// The backend's OAuth2 strict-mode family revocation would kill the family on the second call,
// causing both tabs to be logged out. We coordinate across tabs via BroadcastChannel:
// when one tab starts refreshing, others mark themselves as refreshing and queue their 401s
// instead of issuing their own refresh request. The winner broadcasts the new token via storage
// (already happens via localStorage.setItem) and a 'refresh-success' event so peers can resume.
type AuthBroadcastMessage =
  | { type: 'refresh-start' }
  | { type: 'refresh-success'; access_token: string }
  | { type: 'refresh-failed' };

let authChannel: BroadcastChannel | null = null;
try {
  if (typeof BroadcastChannel !== 'undefined') {
    authChannel = new BroadcastChannel('krp-auth');
    authChannel.onmessage = (event: MessageEvent<AuthBroadcastMessage>) => {
      const msg = event.data;
      if (!msg || typeof msg !== 'object') return;

      if (msg.type === 'refresh-start') {
        // Peer started refreshing — single-flight ฝั่งเราจะ dedup เองเมื่อเจอ 401
        // (await refreshTokenSingleFlight แทน). ไม่ต้องตั้ง flag อะไรที่นี่
        console.log('🔄 Peer tab started refresh');
      } else if (msg.type === 'refresh-success') {
        // Peer finished refreshing — new tokens already in localStorage via setItem.
        // Drain our queue with the new access token.
        console.log('✅ Peer tab refresh succeeded — draining queue with new token');
        processQueue(null, msg.access_token);
      } else if (msg.type === 'refresh-failed') {
        // Peer's refresh failed ด้วย AUTH error (เราจะ broadcast refresh-failed เฉพาะกรณี auth เท่านั้น)
        // → reject queue + logout ตามทั้งครอบครัว
        console.log('❌ Peer tab refresh failed (auth) — forcing logout');
        processQueue(new Error('Peer refresh failed'), null);
        forceLogout();
      }
    };
  }
} catch (err) {
  // BroadcastChannel not available (old browser / private mode) — degrade gracefully
  console.warn('BroadcastChannel unavailable; cross-tab refresh coordination disabled', err);
  authChannel = null;
}

const broadcastAuth = (msg: AuthBroadcastMessage) => {
  try {
    authChannel?.postMessage(msg);
  } catch (err) {
    // Swallow — coordination is a best-effort enhancement, not a hard dependency
    console.warn('Failed to broadcast auth event', err);
  }
};

// BUG-S2001-001 (KD3 S2001, 2026-09-02): หลัง token ถูก invalidate ทั้งชุด (เช่น เปลี่ยนรหัสผ่าน /
// revoke) หน้าที่ยิงหลาย request เป็น wave จะเจอ 401 ซ้ำหลายรอบ → แต่ละรอบ refresh ล้มแล้วเรียก
// forceLogout ใหม่ → toast "เซสชันหมดอายุ" ซ้อนกัน 10+ ใบ · guard ให้ logout/แจ้งครั้งเดียว
// (flag reset เองตอน redirect เพราะ page reload)
let logoutInProgress = false;

const forceLogout = (reasonMessage?: string) => {
  if (logoutInProgress) {
    return;
  }
  logoutInProgress = true;

  // Clear all auth data
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('token_expires_at');
  localStorage.removeItem('user');
  localStorage.removeItem('permissions');

  // เอ๊ะ #7: ถ้ารู้สาเหตุจริง (บทบาท/บัญชีถูกปิด) ให้บอกสาเหตุนั้น แทนข้อความ "เซสชันหมดอายุ"
  // ที่ทำให้ผู้ใช้เข้าใจผิดว่าแค่ login ใหม่ก็จบ
  if (reasonMessage) {
    showAlert('ไม่สามารถเข้าใช้งานได้', reasonMessage, 'error');
  } else {
    showAlert('เซสชันหมดอายุ', 'กรุณาเข้าสู่ระบบใหม่', 'error');
  }

  setTimeout(() => {
    window.location.href = '/login';
  }, 1500);
};

// ==================== FIX (refresh single-flight + logout-guard, 2026-06): items #2 + #3 ====================
// ปัญหาเดิม: มี refresh path 3 ทางที่ไม่ประสานกัน (reactive 401 interceptor, proactive request
// interceptor, และ authService.refreshAccessToken/validateToken) → สอง path อาจยิง /auth/refresh
// ด้วย refresh token เดิมพร้อมกัน → backend ทำ family revocation → ผู้ใช้หลุดทั้งครอบครัว
//
// แก้: รวมทุก path ให้ผ่าน refreshTokenSingleFlight() ตัวเดียว — ถ้ามี refresh ค้างอยู่จะคืน
// promise เดิม (ไม่ยิงซ้ำ). นอกจากนี้แยกผลลัพธ์เป็น auth-failure (ต้อง logout) vs transient
// (network/timeout/429/5xx — ไม่ logout, ให้ retry ครั้งถัดไป).

/** ผลลัพธ์ของการ refresh — discriminated union เพื่อให้ caller ตัดสินใจ logout ได้ถูกต้อง */
export type RefreshOutcome =
  | { ok: true; accessToken: string }
  | { ok: false; reason: 'auth' | 'transient' };

// Single-flight: promise ที่กำลัง refresh อยู่ (null = ว่าง)
let refreshInFlight: Promise<RefreshOutcome> | null = null;

/** จำแนก error จาก /auth/refresh: 401/403 = auth (ต้อง logout), ที่เหลือ = transient */
const classifyRefreshError = (error: any): 'auth' | 'transient' => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    return 'auth';
  }
  // network error / timeout / no response / 429 / 5xx → transient (อย่า logout)
  return 'transient';
};

/** ทำ refresh จริง (เรียกผ่าน single-flight เท่านั้น) */
const doRefresh = async (): Promise<RefreshOutcome> => {
  // BUG-S2001-001: กำลัง logout อยู่แล้ว → ไม่ต้องยิง /auth/refresh ซ้ำ (จะล้มเหมือนเดิม)
  if (logoutInProgress) {
    return { ok: false, reason: 'auth' };
  }
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    // ไม่มี refresh token = ถือเป็น auth failure (ต้อง login ใหม่)
    console.warn('🔄 No refresh token available');
    return { ok: false, reason: 'auth' };
  }

  // แจ้ง peer tabs ว่าเรากำลัง refresh — ป้องกัน peer ยิง refresh แข่ง (family revocation)
  broadcastAuth({ type: 'refresh-start' });

  try {
    console.log('🔄 Attempting token refresh...');
    const response = await rawAxios.post('/auth/refresh', {
      refresh_token: refreshToken,
    });

    const { access_token, refresh_token: newRefreshToken, expires_in } = response.data;

    // บันทึก token ใหม่
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', newRefreshToken);

    // คำนวณ expires_at (ใน milliseconds)
    const expiresAt = Date.now() + (expires_in * 1000);
    localStorage.setItem('token_expires_at', expiresAt.toString());

    console.log('✅ Token refreshed successfully');
    broadcastAuth({ type: 'refresh-success', access_token });
    return { ok: true, accessToken: access_token };
  } catch (error: any) {
    const reason = classifyRefreshError(error);
    console.error(`❌ Token refresh failed (${reason}):`, error?.response?.status, error?.response?.data);
    if (reason === 'auth') {
      // เฉพาะ auth failure เท่านั้นที่บอก peer ให้ logout — transient ห้าม broadcast refresh-failed
      broadcastAuth({ type: 'refresh-failed' });
    }
    return { ok: false, reason };
  }
};

/**
 * Single source of truth สำหรับ refresh access token.
 * ถ้ามี refresh ค้างอยู่ → คืน promise เดิม (caller ทุกตัว reuse promise เดียวกัน → ยิง refresh ครั้งเดียว)
 */
export const refreshTokenSingleFlight = (): Promise<RefreshOutcome> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = doRefresh()
    .then((outcome) => {
      // Centralized queue drain: ไม่ว่า path ไหนเป็นคนเริ่ม refresh (reactive 401 หรือ proactive
      // fire-and-forget) เมื่อ refresh เสร็จให้ปลุก failedQueue เสมอ — กัน request ที่เจอ 401 แล้ว
      // queue รออยู่เบื้องหลัง "proactive" refresh ค้างตลอดกาล (spinner ไม่จบ). processQueue ตั้ง
      // failedQueue=[] หลังทำงาน จึง idempotent: ถ้ามีใครเรียกซ้ำก็เป็น no-op
      if (outcome.ok === true) {
        processQueue(null, outcome.accessToken);
      } else {
        // reject queued requests (ทั้ง auth และ transient) — การ logout ปล่อยให้ caller/peer ตัดสิน
        processQueue(new Error('refresh failed'), null);
      }
      return outcome;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
};

// ==================== Request Interceptor ====================
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Proactive Refresh: ถ้า token จะหมดอายุใน 2 นาที → refresh ล่วงหน้า
    const expiresAt = localStorage.getItem('token_expires_at');
    if (expiresAt && token) {
      const timeRemaining = parseInt(expiresAt) - Date.now();
      if (timeRemaining < 120000 && timeRemaining > 0) {
        console.log(`🔄 Proactive refresh: token expires in ${Math.round(timeRemaining / 1000)}s`);
        // Fire-and-forget ผ่าน single-flight: ถ้ามี refresh ค้างอยู่จะ reuse promise เดิม
        // (ไม่ block request ปัจจุบัน). transient ignore, auth ปล่อยให้ 401 interceptor จัดการ logout
        refreshTokenSingleFlight().then((outcome) => {
          if (outcome.ok) {
            console.log('🔄 Proactive refresh succeeded');
          }
          // ไม่ logout จาก proactive path — ปล่อยให้ reactive 401 เป็นคนตัดสิน
        }).catch(() => {
          // Ignore — single-flight ไม่ throw แต่กันไว้
        });
      }
    }

    // Handle FormData - ลบ Content-Type เพื่อให้ browser ตั้ง boundary สำหรับ multipart/form-data
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      console.log(`📎 FormData detected - Content-Type will be set automatically`);
    }

    // Log outgoing request
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);

    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// ==================== Response Interceptor ====================
// เอ๊ะ #7: กันแจ้งซ้ำเมื่อหลาย request โดน 403 พร้อมกันหลังบทบาทถูกปิด
let accessBlockedNotified = false;
// P-S043-1: หน้าหนึ่งยิงหลาย request พร้อมกัน — เน็ตหลุดทีเดียวทุกตัว fail พร้อมกัน
// ต้อง throttle toast network error ไม่ให้ซ้อนกันเป็นตับ
let lastNetworkErrorToastAt = 0;

api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // OBS-R129-001: a first 401 that will trigger a silent refresh+retry is expected and gets
    // recovered — log it quietly (debug) to avoid console/Sentry error-noise on token expiry.
    // Genuine auth failures (401 after a retry, or 401 on /auth/login) still log as error below.
    const willRefresh401 = error.response?.status === 401 && !originalRequest?._retry
      && !(originalRequest?.url || '').includes('/auth/login');

    // Log error details
    if (error.response) {
      if (willRefresh401) {
        console.debug(`🔄 API 401 (auto-refreshing): ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
      } else {
        console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response.status}`);
        console.error('Error data:', error.response.data);

        if (error.response.data && typeof error.response.data === 'object') {
          const errorData = error.response.data as any;
          if (errorData.error) {
            console.error('Error details:', errorData.error);
          }
          if (errorData.detail) {
            console.error('Error detail:', errorData.detail);
          }
        }
      }
    } else if (error.request) {
      console.error('❌ No response received from API:', error.request);
    } else {
      console.error('❌ Request setup error:', error.message);
    }

    // Handle 401 Unauthorized — ลอง refresh ก่อน logout
    // ยกเว้น /auth/login: 401 จาก login หมายถึง credentials ผิด ไม่ใช่ session หมดอายุ
    const requestUrl = originalRequest.url || '';
    const isLoginRequest = requestUrl.includes('/auth/login');

    if (error.response?.status === 401 && !originalRequest._retry && !isLoginRequest) {
      originalRequest._retry = true;

      // ถ้ามี refresh ค้างอยู่ (single-flight) → queue request นี้ไว้รอ token ใหม่
      // single-flight dedup ตัว refresh เอง แต่ queue ยังจำเป็นเพื่อ retry request เดิมหลัง refresh เสร็จ
      if (refreshInFlight) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      // เริ่ม refresh ผ่าน single-flight (broadcast refresh-start + drain failedQueue ทำภายใน single-flight)
      const outcome = await refreshTokenSingleFlight();

      if (outcome.ok === true) {
        // Refresh สำเร็จ → retry request ของเราเอง (failedQueue ถูก drain โดย single-flight แล้ว)
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${outcome.accessToken}`;
        }
        return api(originalRequest);
      } else if (outcome.reason === 'auth') {
        // refresh token หมดอายุ/ถูก revoke จริง → logout (failedQueue ถูก reject โดย single-flight แล้ว;
        // broadcast refresh-failed ถูกยิงภายใน doRefresh เพื่อให้ peer tabs logout ด้วย)
        forceLogout();
      }
      // transient (network/timeout/429/5xx) → ไม่ logout/ไม่ล้าง token; ปล่อย error เดิม reject ออกไป
      // (failedQueue ถูก reject โดย single-flight แล้ว) — request ถัดไปจะลอง refresh ใหม่เอง
    }

    // เอ๊ะ #7 (PO-approved 2026-08-19): 403 ที่มาจาก "บัญชี/บทบาทถูกปิด" ไม่ใช่ "ปุ่มนี้ไม่มีสิทธิ์"
    // — ถ้าปล่อยให้ผู้ใช้เดินต่อบน dashboard ที่ 403 ทุกจุด เขาจะไล่กดทีละหน้าแล้วโทรหา support
    // ด้วยอาการผิด · backend ส่ง reason มาให้แล้ว (ROLE_DISABLED / USER_INACTIVE) → แจ้งครั้งเดียว
    // แบบชัดๆ แล้วพากลับไปหน้า login
    if (error.response?.status === 403) {
      const _d = (error.response.data as any)?.error?.details;
      const _reason = _d?.reason;
      // BUG-S348-ROLEDISABLED-MSG FIX: การ "ตัดเซสชัน" ใช้กับเซสชันที่เปิดอยู่เท่านั้น —
      // ถ้า 403 นี้มาจาก "คำขอ login" (ยังไม่มีเซสชันให้ตัด) การ forceLogout จะ reload หน้า
      // ทิ้งใน 1.5 วิ ⇒ ข้อความหายไปต่อหน้าต่อตา เหลือฟอร์มเปล่า · ปล่อยให้หน้า login
      // แสดง error ของตัวเองแบบค้างไว้แทน
      const _isLoginCall = /\/auth\/login/.test(error.config?.url || '');
      if (!_isLoginCall && (_reason === 'ROLE_DISABLED' || _reason === 'USER_INACTIVE') && !accessBlockedNotified) {
        accessBlockedNotified = true;
        const _msg = _d?.message
          || (_reason === 'ROLE_DISABLED'
                ? 'บทบาทของคุณถูกปิดใช้งาน กรุณาติดต่อผู้ดูแลระบบ'
                : 'บัญชีของคุณถูกปิดใช้งาน กรุณาติดต่อผู้ดูแลระบบ');
        try {
          window.dispatchEvent(new CustomEvent('krp:access-blocked', { detail: { reason: _reason, message: _msg } }));
        } catch { /* ignore */ }
        forceLogout(_msg);
      }
    }

    // P-S043-1: ยิงไม่ถึง server (offline/timeout/DNS) — เดิมผู้ใช้เห็น raw "Network Error"
    // หรือ fallback generic ของแต่ละหน้า → toast ไทยกลางจุดเดียว ครอบทุก call site
    // · ข้าม login (หน้า login มี error ค้างของตัวเอง) และ request ที่ถูก cancel
    const _isCancelled = axios.isCancel(error as unknown);
    if (!error.response && !_isCancelled) {
      const _url = error.config?.url || '';
      const _now = Date.now();
      if (!/\/auth\/login|\/health/.test(_url) && _now - lastNetworkErrorToastAt > 5000) {
        lastNetworkErrorToastAt = _now;
        const _netMsg = (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || ''))
          ? NETWORK_ERROR_MESSAGES[1]
          : NETWORK_ERROR_MESSAGES[0];
        try { showError(_netMsg); } catch { /* toast host ยังไม่ mount — ปล่อยผ่าน */ }
      }
    }

    return Promise.reject(error);
  }
);

// API call options interface
interface ApiCallOptions {
  service: string;
  action?: string;
  entityType?: string;
  entityId?: number;
  skipLogging?: boolean;
  details?: any;
}

// API methods with logging
class ApiWithLogging {
  // GET request
  static async get<T = any>(
    url: string,
    config: AxiosRequestConfig = {},
    options?: ApiCallOptions
  ): Promise<T> {
    if (!options || options.skipLogging) {
      const response = await api.get<T>(url, config);
      return response.data;
    }

    const response = await Logger.wrapApiCall(
      options.service,
      'GET',
      url,
      () => {
        if (options.action && options.entityType) {
          Logger.logUserAction(
            options.action,
            options.entityType,
            options.entityId,
            { url, params: config.params },
            'pending'
          );
        }
        return api.get<T>(url, config);
      },
      config.params
    );
    return response.data;
  }

  // POST request
  static async post<T = any>(
    url: string,
    data?: any,
    config: AxiosRequestConfig = {},
    options?: ApiCallOptions
  ): Promise<T> {
    if (!options || options.skipLogging) {
      const response = await api.post<T>(url, data, config);
      return response.data;
    }

    const response = await Logger.wrapApiCall(
      options.service,
      'POST',
      url,
      async () => {
        if (options.action && options.entityType) {
          Logger.logUserAction(
            options.action,
            options.entityType,
            options.entityId,
            { url, data },
            'pending'
          );
        }

        const result = await api.post<T>(url, data, config);

        if (options.action && options.entityType) {
          Logger.logUserAction(
            options.action,
            options.entityType,
            (result.data as any)?.id || options.entityId,
            { url, data },
            'success'
          );
        }

        return result;
      },
      config.params,
      data
    );
    return response.data;
  }

  // PUT request
  static async put<T = any>(
    url: string,
    data?: any,
    config: AxiosRequestConfig = {},
    options?: ApiCallOptions
  ): Promise<T> {
    if (!options || options.skipLogging) {
      const response = await api.put<T>(url, data, config);
      return response.data;
    }

    const response = await Logger.wrapApiCall(
      options.service,
      'PUT',
      url,
      async () => {
        if (options.action && options.entityType) {
          Logger.logUserAction(
            options.action,
            options.entityType,
            options.entityId,
            { url, data },
            'pending'
          );
        }

        const result = await api.put<T>(url, data, config);

        if (options.action && options.entityType) {
          Logger.logUserAction(
            options.action,
            options.entityType,
            options.entityId,
            { url, data },
            'success'
          );
        }

        return result;
      },
      config.params,
      data
    );
    return response.data;
  }

  // DELETE request
  static async delete<T = any>(
    url: string,
    config: AxiosRequestConfig = {},
    options?: ApiCallOptions
  ): Promise<T> {
    if (!options || options.skipLogging) {
      const response = await api.delete<T>(url, config);
      return response.data;
    }

    const response = await Logger.wrapApiCall(
      options.service,
      'DELETE',
      url,
      async () => {
        if (options.action && options.entityType) {
          Logger.logUserAction(
            options.action,
            options.entityType,
            options.entityId,
            { url },
            'pending'
          );
        }

        const result = await api.delete<T>(url, config);

        if (options.action && options.entityType) {
          Logger.logUserAction(
            options.action,
            options.entityType,
            options.entityId,
            { url },
            'success'
          );
        }

        return result;
      },
      config.params
    );
    return response.data;
  }

  // PATCH request
  static async patch<T = any>(
    url: string,
    data?: any,
    config: AxiosRequestConfig = {},
    options?: ApiCallOptions
  ): Promise<T> {
    if (!options || options.skipLogging) {
      const response = await api.patch<T>(url, data, config);
      return response.data;
    }

    const response = await Logger.wrapApiCall(
      options.service,
      'PATCH',
      url,
      async () => {
        if (options.action && options.entityType) {
          Logger.logUserAction(
            options.action,
            options.entityType,
            options.entityId,
            { url, data },
            'pending'
          );
        }

        const result = await api.patch<T>(url, data, config);

        if (options.action && options.entityType) {
          Logger.logUserAction(
            options.action,
            options.entityType,
            options.entityId,
            { url, data },
            'success'
          );
        }

        return result;
      },
      config.params,
      data
    );
    return response.data;
  }
}

// Health check function
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    const healthApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: 5000
    });

    await healthApi.get('/health');
    return true;
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
};

// Export both the raw axios instance and the logging wrapper
export { api as rawApi, rawAxios, forceLogout };
export default ApiWithLogging;
